import React, { useState, useCallback, useEffect, useRef } from "react";
import { Button, Flex, Typography, message } from "antd";
import { CloseOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../shared/utils/translationUtils";
const { Text } = Typography;
interface EmailInputWithTagsProps {
  onSendEmails: (emails: string[]) => void;
}
const EmailInputWithTags: React.FC<EmailInputWithTagsProps> = ({
  onSendEmails,
}) => {
  const { t } = useTranslation();
  const [emailInput, setEmailInput] = useState("");
  const [emailList, setEmailList] = useState<string[]>([]);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  useEffect(() => {
    setIsEmailValid(emailRegex.test(emailInput.trim()));
  }, [emailInput]);
  const handleAddEmail = useCallback(() => {
    const email = emailInput.trim();
    if (emailRegex.test(email)) {
      setEmailList((prev) => [...prev, email]);
      setEmailInput("");
      inputRef.current?.focus();
    }
  }, [emailInput]);
  const handleRemoveEmail = useCallback((emailToRemove: string) => {
    setEmailList((prev) => prev.filter((email) => email !== emailToRemove));
    inputRef.current?.focus();
  }, []);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (["Enter", ",", " "].includes(e.key)) {
        e.preventDefault();
        if (emailInput.trim()) handleAddEmail();
      } else if (e.key === "Backspace" && !emailInput) {
        setEmailList((prev) => prev.slice(0, -1));
      }
    },
    [emailInput, handleAddEmail]
  );
  const handleSend = useCallback(async () => {
    if (emailList.length === 0) return;
    setIsSending(true);
    try {
      onSendEmails(emailList);
      setEmailList([]);
    } catch (error) {
      void message.error(formatTranslation(t("report.trial.emailSendFailed")));
    } finally {
      setIsSending(false);
    }
  }, [emailList, onSendEmails]);
  // Calculate visible tags based on available space
  const calculateVisibleTags = (): {
    visibleTags: string[];
    overflowCount: number;
  } => {
    if (!containerRef.current) return { visibleTags: [], overflowCount: 0 };
    const containerWidth = containerRef.current.offsetWidth - 100; // Account for button
    const approxTagWidth = 120; // Approximate width per tag
    const maxVisible = Math.max(1, Math.floor(containerWidth / approxTagWidth));
    return {
      visibleTags: emailList.slice(0, maxVisible),
      overflowCount: Math.max(0, emailList.length - maxVisible),
    };
  };
  const { visibleTags, overflowCount } = calculateVisibleTags();
  return (
    <Flex vertical gap={8}>
      <h4 style={{ marginBottom: 8 }}>
        {formatTranslation(t("report.email.title"))}
      </h4>
      <Text type="secondary" style={{ color: "#696F88" }}>
        {formatTranslation(t("report.email.placeholder"))}
      </Text>
      <Flex ref={containerRef} align="center" gap={8} style={{ width: "100%" }}>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: "4px",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            padding: "4px 8px",
            minHeight: "32px",
            backgroundColor: "#fff",
            cursor: "text",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
          onClick={() => inputRef.current?.focus()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.focus();
            }
          }}
          role="button"
          tabIndex={0}
        >
          {visibleTags.map((email, index) => (
            <div
              key={`${email}-${index}`}
              style={{
                display: "flex",
                alignItems: "center",
                background: "#f0f0f0",
                borderRadius: "4px",
                padding: "0 8px",
                height: "24px",
                fontSize: "12px",
                flexShrink: 0,
              }}
            >
              <span style={{ marginRight: "4px", whiteSpace: "nowrap" }}>
                {email}
              </span>
              <CloseOutlined
                style={{ fontSize: "10px", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveEmail(email);
                }}
              />
            </div>
          ))}
          {overflowCount > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: "#f0f0f0",
                borderRadius: "4px",
                padding: "0 8px",
                height: "24px",
                fontSize: "12px",
                flexShrink: 0,
              }}
            >
              +{overflowCount}
            </div>
          )}
          <input
            ref={inputRef}
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              isEmailValid && handleAddEmail();
            }}
            placeholder={
              emailList.length === 0
                ? formatTranslation(t("report.email.enterAddresses"))
                : ""
            }
            style={{
              flex: 1,
              minWidth: "100px",
              border: "none",
              outline: "none",
              height: "24px",
              padding: "0 4px",
              backgroundColor: "transparent",
            }}
          />
        </div>
        <Button
          className={emailList.length > 0 ? "next-btn" : ""}
          icon={<SendOutlined />}
          onClick={() => {
            void handleSend();
          }}
          loading={isSending}
          disabled={emailList.length === 0}
          style={{ height: "32px", flexShrink: 0 }}
        >
          {formatTranslation(t("report.email.send"))}
        </Button>
      </Flex>
      {!isEmailValid && emailInput && (
        <Text className="danger" type="danger" style={{ fontSize: "12px" }}>
          {formatTranslation(t("report.email.validation"))}
        </Text>
      )}
    </Flex>
  );
};
export default EmailInputWithTags;
